import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { AppLayout } from './layouts/AppLayout';
import { AuthComplete } from './pages/AuthComplete';
import { Dashboard } from './pages/Dashboard';
import { Login } from './pages/Login';
import { SessionDetail } from './pages/SessionDetail';
import { Sessions } from './pages/Sessions';
import { Settings } from './pages/Settings';
import { Setup } from './pages/Setup';
import { Projects } from './pages/Projects';
import { Reports } from './pages/Reports';
import { Stats } from './pages/Stats';
import { Insights } from './pages/Insights';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000, // 30 seconds
      retry: 1,
    },
  },
});

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isLoading, isAuthenticated } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream dark:bg-navy-950">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-navy-500 dark:text-navy-300">Loading…</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login with return URL
    const returnUrl = encodeURIComponent(window.location.pathname);
    return <Navigate to={`/login?redirect_uri=${returnUrl}`} replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/auth-complete" element={<AuthComplete />} />

      {/* Protected routes */}
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<Dashboard />} />
        <Route path="/sessions" element={<Sessions />} />
        <Route path="/sessions/:sessionId" element={<SessionDetail />} />
        {/* Prompts page was removed (#777); keep a redirect for bookmarked links. */}
        <Route path="/prompts" element={<Navigate to="/" replace />} />
        <Route path="/insights" element={<Insights />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/stalls" element={<Navigate to="/sessions" replace />} />
        <Route path="/memory" element={<Navigate to="/insights?tab=patterns" replace />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/digest" element={<Navigate to="/reports" replace />} />
        <Route path="/snippets" element={<Navigate to="/reports" replace />} />
        <Route path="/tasks" element={<Navigate to="/settings?tab=admin" replace />} />
        <Route path="/stats" element={<Stats />} />
        <Route path="/setup" element={<Setup />} />
        <Route path="/settings" element={<Settings />} />
      </Route>

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
